// src/services/wealthSync.service.ts
// Production-safe global event emitter with debounce

type Listener = () => void;

interface NamedListener {
  id: string;
  listener: Listener;
}

class WealthSyncService {
  private listeners = new Map<string, Listener>();
  private lastNotifyTime: number = 0;
  private notifyTimeout: ReturnType<typeof setTimeout> | null = null;
  private readonly DEBOUNCE_MS = 300; // Prevent rapid-fire notifications

  subscribe(listener: Listener): () => void {
    // Generate a random ID for anonymous listeners
    const id = `anon_${Math.random().toString(36).slice(2, 11)}`;
    return this.subscribeById(id, listener);
  }

  subscribeById(id: string, listener: Listener): () => void {
    this.listeners.set(id, listener);
    console.log('[WealthSync] Listener added:', id, 'Total:', this.listeners.size);
    return () => {
      const had = this.listeners.delete(id);
      if (had) {
        console.log('[WealthSync] Listener removed:', id, 'Total:', this.listeners.size);
      }
    };
  }

  notify(): void {
    const now = Date.now();

    // Debounce: ignore notifications within 300ms of each other
    if (now - this.lastNotifyTime < this.DEBOUNCE_MS) {
      console.log('[WealthSync] Debounced - too soon since last notify');
      return;
    }

    this.lastNotifyTime = now;

    // Clear any pending timeout
    if (this.notifyTimeout) {
      clearTimeout(this.notifyTimeout);
    }

    // Small delay to ensure DB transaction is fully committed
    this.notifyTimeout = setTimeout(() => {
      console.log('[WealthSync] Notifying', this.listeners.size, 'listeners');
      this.listeners.forEach(listener => {
        try {
          listener();
        } catch (e) {
          console.error('[WealthSync] Listener error:', e);
        }
      });
    }, 100);
  }

  getListenerCount(): number {
    return this.listeners.size;
  }
}

export const wealthSync = new WealthSyncService();
